import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DisplayOption } from '../list-options';
import { MediaService } from '@app/core/services/media.service';
import { AddedTrigger, SelectedActionElement } from '@app/smart-actions/types/smart-actions';

@Component({
    selector: 'journey-details',
    templateUrl: './journey-details.component.html',
    styleUrls: ['./journey-details.component.scss'],
    standalone: false
})
export class JourneyDetailsComponent implements OnInit {
  @Input() journeyRecord: any;
  @Input() messageView;
  @Input() displayOption: DisplayOption;
  @Input() selectionType: string = null;
  @Input() copyType: string = null;
  @Input() copyMsgParams: any = null;
  @Input() includeSmsOnlyMessages: boolean = true;

  @Input() selectedStepId: string | null = null;
  @Output() stepSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() stepActionSelected: EventEmitter<any> = new EventEmitter<any>();

  ulWidth: number;
  fetchingAsset: boolean;

  constructor(private mediaService: MediaService) {
  }

  ngOnInit() {
    const messages = this.journeyRecord.latestContent.components.filter(c => (c.type === 'MessageSender') || (c.type === 'MessageResponse'));
    this.setAssetURLs(messages);

    if (this.journeyRecord.live) {
      const liveMessages = this.journeyRecord.live.components.filter(c => (c.type === 'MessageSender') || (c.type === 'MessageResponse'));
      this.setAssetURLs(liveMessages);
    }

    this.ulWidth = this.calculateUlWidth(messages);
  }

  copyIdToClipboard(messageId: string, event) {
    event.stopPropagation();
    (<HTMLInputElement> document.getElementById(messageId)).select();
    document.execCommand('copy');
  }

  calculateUlWidth(messages) {
    return messages.length * 300;
  }

  setAssetURLs(messages: any) {
    for (let i = 0; i < messages.length; i++) {
      const wire = messages[i].wire;
      const media_asset = wire.media_asset;
      const media_id = wire.media_id;

      if (media_asset) {
        // template experiences have their assets pre-fetched, so media_id is
        // undefined and media_asset contains the getMediaAsset response
        if (media_asset.asset_type === 'video') {
          wire.video_url = media_asset.url;
        } else {
          wire.image_url = media_asset.s3_url;
        }
      } else if (media_id) {
        this.fetchingAsset = true;
        this.mediaService.getMediaAsset(media_id, true).subscribe(media => {
          if (media.asset_type === 'video') {
            wire.video_url = media.url;
          } else {
            wire.image_url = media.s3_url;
          }
          this.fetchingAsset = false;
        });
      }
    } 
  }

  // For the Smart Actions UI
  onStepSelected(step: any): void {
    this.selectedStepId = step.name;
    const triggerType = this.getTriggerType(step);

    const trigger: AddedTrigger = {
      triggerId: step.name,
      nickname: step.nickname,
      journeyName: this.journeyName,
      journeyId: this.journeyId,
      triggerType: triggerType,
    };

    this.stepSelected.emit(trigger)
  }

  isIntegerStep(step: string): boolean {
    // Check if the step is an integer (e.g., "1", "2")
    return /^\d+$/.test(step);
  }

  isFirstPartOfStep(step: string): boolean {
    // Check if the step is the first part (e.g., 1.a, 2.a)
    const regex = /^[0-9]+\.a$/;
    return regex.test(step);
  }

  shouldHighlightStep(step: any): boolean {
    if (!this.selectedStepId) {
      return false;
    }
  
    // Highlight the selected step and all subsequent steps
    const selectedIndex = this.filteredComponents.findIndex(
      (s) => s.name === this.selectedStepId
    );
    const currentIndex = this.filteredComponents.findIndex(
      (s) => s.name === step.name
    );
    return currentIndex >= selectedIndex;
  }
  
  get filteredComponents() {
    if (this.includeSmsOnlyMessages) {
      return this.journeyContent.components;
    } else {
      return this.journeyContent.components.filter(c => !c.wire || c.wire.is_shown === 'true');
    }
  }

  get journeyContent() {
    if (this.displayOption === DisplayOption.live) {
      return this.journeyRecord.live;
    } else if (this.displayOption === DisplayOption.draft) {
      return this.journeyRecord.draft;
    } else if (this.displayOption === DisplayOption.preferLiveToDraft) {
      return this.journeyRecord.live ? this.journeyRecord.live : this.journeyRecord.draft;
    }
  }

  get journeyName(): string {
    return this.journeyRecord.live?.name ?? 'Untitled Experience';
  }

  get journeyId(): string {
    return this.journeyRecord.id;
  }

  getTriggerType(step: any): string | null {
    if (!step.step || !step.step.includes('.')) {
      return null;
    }
  
    // Extract the parent step (e.g., "3" from "3.a")
    const parentStep = step.step.split('.')[0];
  
    // Find the parent step in the components list
    const parent = this.filteredComponents.find((s) => s.step === parentStep);
    return parent ? parent.type : null;
  }

  onStepActionSelected(event: SelectedActionElement) {
    this.stepActionSelected.emit(event);
  }
}
